import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import _get from 'lodash/get';
import { useEffect, useState } from 'react';
import { getVideoAskFormData, useVideoAskModal } from './lib/video-ask-client';

const VideoAskContainer = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 70%;
  text-align: center;
`;

const VideoAskWidgetInner = styled.span`
  border-radius: 45px;
  border: #ffcf31 solid 6px;
  background: #ffcf31;
  overflow: hidden;
  mask-image: -webkit-radial-gradient(white, black);
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  transform: translateY(0);
  backface-visibility: hidden;
  opacity: 1;
  transition:
    transform 200ms ease-in-out,
    opacity 300ms ease;
`;

const VideoAskBackground = styled.span`
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 35%, rgba(0, 0, 0, 0.4) 100%);
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  z-index: 2;
  transform: translateZ(0);
`;

const VideoAskVideo = styled.video`
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  z-index: 1;
  object-position: 50% 50% !important;
  object-fit: cover !important;
  transform: translateZ(0);
`;

const VideoAskLoader = styled(CircularProgress)`
  display: none;
`;

const VideoAskText = styled.p`
  display: block;
  position: absolute;
  bottom: 0;
  z-index: 3;
  color: white;
  font-size: 1.5rem;
  transform: translateZ(0);
  padding: 0 1rem;
  text-align: center;
`;

const VideoAskWidget = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: transparent;
  border: none;
  cursor: pointer;

  &.loading {
    ${VideoAskWidgetInner} {
      opacity: 0;
      transform: translateY(50px);
    }
    ${VideoAskLoader} {
      display: block;
    }
  }

  :hover {
    ${VideoAskWidgetInner} {
      transform: scale(1.05);
    }
  }
`;

interface VideoAskProps {
  url: string;
  title: string;
  text?: string;
  muted?: boolean;
  autoPlay?: boolean;
  openModalOnClick?: boolean;
  fullscreen?: boolean;
  variables?: Record<string, string>;
}

export const VideoAsk = ({
  url,
  text,
  title,
  variables,
  muted,
  autoPlay,
  openModalOnClick,
  fullscreen,
}: VideoAskProps) => {
  const [metadataText, setMetadataText] = useState<string>();
  const [videoUrl, setVideoUrl] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [openVideoAskModal] = useVideoAskModal(url, variables, {
    modalType: fullscreen ? 'Fullscreen' : 'Side',
  });

  const videoText = text || metadataText || '';

  useEffect(() => {
    getVideoAskFormData(url).then(data => {
      const mediaUrl = _get(data, ['questions', 0, 'media_url']);
      if (mediaUrl) {
        setVideoUrl(mediaUrl);
      }
      const metadataText = _get(data, ['questions', 0, 'metadata', 'text']);
      if (mediaUrl) {
        setMetadataText(metadataText);
      }
    });
  }, [url, text]);

  return (
    <VideoAskContainer>
      <VideoAskWidget
        role="button"
        className={loading ? 'loading' : ''}
        aria-label={title}
        onClick={
          openModalOnClick === undefined || openModalOnClick === true ? openVideoAskModal : () => {}
        }
      >
        <VideoAskWidgetInner>
          {videoUrl && (
            <VideoAskVideo
              muted={muted ?? true}
              autoPlay={autoPlay ?? true}
              loop
              playsInline
              src={videoUrl}
              onLoadedData={() => setLoading(false)}
            />
          )}
          <VideoAskText>{videoText}</VideoAskText>
          <VideoAskBackground />
        </VideoAskWidgetInner>
        <VideoAskLoader />
      </VideoAskWidget>
    </VideoAskContainer>
  );
};
